import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  fetchMapFeatures,
  fetchHeatmap,
  type MapFeatureCollection,
  type HeatmapFeatureCollection,
} from "../api/map";

export function useMapFeatures(bbox: [number, number, number, number] | null, enabled = true) {
  const [debouncedBbox, setDebouncedBbox] = useState(bbox);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedBbox(bbox), 300);
    return () => clearTimeout(timer);
  }, [bbox]);

  const features = useQuery<MapFeatureCollection>({
    queryKey: ["map/features", debouncedBbox],
    queryFn: () => fetchMapFeatures(debouncedBbox!),
    enabled: enabled && debouncedBbox !== null,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });

  const heatmap = useQuery<HeatmapFeatureCollection>({
    queryKey: ["map/heatmap"],
    queryFn: fetchHeatmap,
    enabled,
    // The API answers with an ETag and `Cache-Control: private, no-cache`, so
    // a refetch here is a browser-cache revalidation: unchanged traces come
    // back as a 304 and the cached body is reused, with no re-download.
    staleTime: 300_000,
  });

  return {
    features: features.data ?? null,
    heatmap: heatmap.data ?? null,
    /** Features (pins/lines for the viewport) are fetching. */
    isLoading: features.isFetching,
    /** First load of the user's traces only: false for background refetches
     *  and once a request has failed. */
    tracesLoading: heatmap.isFetching && heatmap.data === undefined,
  };
}
